import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { UserPlus, Shield, X, Trash2, Pause, Play, Zap, ChevronDown, Smartphone, ScanLine, BarChart3, Package, FileSpreadsheet, Users as UsersIcon, Printer, Settings2, Receipt, Tag, Download, Palette, CalendarDays, Banknote, MapPin } from "lucide-react";

const PERMS = [
  { key: "cassa", label: "Cassa", icon: ScanLine, desc: "Modulo cassa sul tablet (app APK). Non si apre dal sito web." },
  { key: "palmare", label: "Palmare comande", icon: Smartphone, desc: "App mobile per camerieri" },
  { key: "chiusura_fiscale", label: "Chiusura scontrini", icon: Receipt, desc: "Emettere scontrini fiscali e non" },
  { key: "sconti", label: "Sconti e storno", icon: Tag, desc: "Applicare sconti, storni e correzioni" },
  { key: "gestionale", label: "Dashboard gestionale", icon: BarChart3, desc: "Accesso area amministrativa" },
  { key: "magazzino", label: "Magazzino", icon: Package, desc: "Inventario e movimenti scorte" },
  { key: "fatturazione", label: "Fatturazione", icon: FileSpreadsheet, desc: "Generare e gestire fatture" },
  { key: "statistiche", label: "Statistiche", icon: BarChart3, desc: "Visualizzare report e analytics" },
  { key: "esportazioni", label: "Esportazioni ERP", icon: Download, desc: "Export CSV/JSON fine giornata" },
  { key: "stampanti", label: "Stampanti", icon: Printer, desc: "Configurare stampanti fiscali" },
  { key: "branding", label: "Branding scontrino", icon: Palette, desc: "Logo e personalizzazione" },
  { key: "utenti", label: "Gestione utenti", icon: UsersIcon, desc: "Creare/modificare staff" },
  { key: "appuntamenti", label: "Appuntamenti", icon: CalendarDays, desc: "Calendario appuntamenti con selezione sede" },
  { key: "can_view_purchase_prices", label: "Visualizza prezzo di acquisto", icon: Banknote, desc: "Nel magazzino vede anche il prezzo di acquisto degli articoli (richiede accesso Magazzino). Permesso: can_view_purchase_prices." },
];

const RESTO_MODES = ["ristorante", "bar", "pizzeria", "festa"];
const APPT_MODES = ["toelettatura", "parrucchiere", "negozio_animali"];

function permOffered(key, tenantModes) {
  const m = Array.isArray(tenantModes) ? tenantModes : [];
  if (key === "palmare") return m.some((x) => RESTO_MODES.includes(x));
  if (key === "appuntamenti") return m.some((x) => APPT_MODES.includes(x));
  return true;
}

export default function Staff() {
  const { user, setSession } = useAuth();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [tenantModes, setTenantModes] = useState([]);
  const [presets, setPresets] = useState({});
  const [stores, setStores] = useState([]);

  const load = () => api.get("/staff").then((r) => setItems(Array.isArray(r.data) ? r.data : []));

  useEffect(() => {
    load();
    api.get("/staff/permissions").then(r => setPresets(r.data?.role_presets || {}));
    api.get("/tenant/me").then(r => setTenantModes(Array.isArray(r.data?.modes) ? r.data?.modes : []));
    api.get("/stores").then(r => setStores(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const save = async (data) => {
    try {
      const principal = data.role === "client_owner" || data.role === "client";
      if (!principal && !(Array.isArray(data.permissions) && data.permissions.length)) {
        toast.error("Attiva almeno un modulo (cassa, calendario, magazzino…)");
        return;
      }
      const body = { name: (data.name || "").trim() };
      if (data.email) body.email = data.email.trim();
      if (data.password) body.password = data.password;
      if (data.pin) body.pin = data.pin;
      else if (data.clear_pin) body.pin = "";
      if (principal) {
        await api.patch(`/staff/${data.id}`, body);
        const token = localStorage.getItem("velta_token");
        if (user?.id === data.id && token) {
          setSession(token, { ...user, name: body.name || user.name, email: body.email || user.email });
        }
        toast.success("Titolare aggiornato");
      } else if (data.id) {
        body.role = "client_staff";
        body.permissions = data.permissions || [];
        body.allowed_modes = data.allowed_modes ?? null;
        body.store_ids = data.store_ids == null ? [] : data.store_ids;
        body.disabled = !!data.disabled;
        await api.patch(`/staff/${data.id}`, body);
        toast.success("Utente aggiornato");
      } else {
        await api.post("/staff", {
          ...body,
          email: (data.email || "").trim(),
          password: data.password,
          role: "client_staff",
          permissions: data.permissions || [],
          allowed_modes: data.allowed_modes ?? null,
          store_ids: data.store_ids || null,
        });
        toast.success("Utente creato");
      }
      setEditing(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const setDisabled = async (u, disabled) => {
    if (!window.confirm(disabled
      ? `Disattivare «${u.name}»? Non entra più. Resta in elenco e lo puoi riattivare. Gli scontrini già fatti restano.`
      : `Riattivare «${u.name}»?`)) return;
    try {
      await api.patch(`/staff/${u.id}`, { disabled });
      toast.success(disabled ? "Utente disattivato" : "Utente riattivato");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const del = async (u) => {
    if (!window.confirm(`Cancellare «${u.name}»? Non potrà più entrare. Gli scontrini già emessi restano. Non si annulla.`)) return;
    try { await api.delete(`/staff/${u.id}`); toast.success("Utente cancellato"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="p-8 lg:p-12 max-w-6xl">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-velta-mute mb-3">Team</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight">Utenti & Permessi</h1>
          <p className="text-velta-mute mt-2">Dal tuo account di titolare puoi modificare nome, email e password, oppure disattivare e cancellare un utente che hai creato. Il titolare non si cancella. Gli scontrini già emessi restano.</p>
        </div>
        <button data-testid="staff-new-btn" onClick={() => setEditing({ email: "", password: "", name: "", role: "client_staff", permissions: [], allowed_modes: null })} className="h-12 px-5 bg-[#1E2D5B] hover:bg-[#162244] text-white rounded-md font-medium flex items-center gap-2 btn-tactile">
          <UserPlus className="w-4 h-4" /> Nuovo utente
        </button>
      </div>

        <div className="bg-white border border-velta-border rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[980px]">
          <thead className="bg-velta-bg border-b border-velta-border">
            <tr className="text-left text-xs font-mono uppercase tracking-widest text-velta-mute">
              <th className="px-5 py-4">Nome</th>
              <th className="px-5 py-4">Email</th>
              <th className="px-5 py-4">Ruolo</th>
              <th className="px-5 py-4">Permessi</th>
              <th className="px-5 py-4">Modalità</th>
              <th className="px-5 py-4">Sedi</th>
              <th className="px-5 py-4">PIN</th>
              <th className="px-5 py-4">Stato</th>
            </tr>
          </thead>
          <tbody>
            {(Array.isArray(items) ? items : []).map(u => {
              const isOwner = u.role === "client_owner" || u.role === "client";
              const perms = isOwner
                ? (Array.isArray(PERMS) ? PERMS : []).filter((p) => permOffered(p.key, tenantModes)).map(p => p.key)
                : (Array.isArray(u.permissions) ? u.permissions : []);
              const modesText = isOwner
                ? (tenantModes.length ? tenantModes.join(", ") : "Contratto")
                : (u.allowed_modes == null ? "Tutte" : (u.allowed_modes.length === 0 ? "Nessuna" : `${u.allowed_modes.length} modalità`));
              return (
                <tr key={u.id} className="border-b border-velta-border last:border-0 hover:bg-velta-bg">
                  <td className="px-5 py-4">
                    <div className="font-medium">{u.name}</div>
                    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                      <button data-testid={`staff-edit-${u.id}`} onClick={() => setEditing({ ...u, password: "", pin: "", clear_pin: false })} className="text-xs font-medium text-[#1E2D5B] hover:underline">Modifica</button>
                      {!isOwner && (
                        <>
                          <button type="button" data-testid={`staff-disable-${u.id}`} onClick={() => setDisabled(u, !u.disabled)} className="text-xs font-medium text-[#1E2D5B] hover:underline inline-flex items-center gap-1 whitespace-nowrap">
                            {u.disabled ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
                            {u.disabled ? "Riattiva" : "Disattiva"}
                          </button>
                          <button type="button" data-testid={`staff-delete-${u.id}`} onClick={() => del(u)} className="text-xs font-medium text-velta-error hover:underline inline-flex items-center gap-1 whitespace-nowrap">
                            <Trash2 className="w-3.5 h-3.5" /> Elimina
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-4 text-velta-mute">{u.email}</td>
                  <td className="px-5 py-4">
                    <span className={`inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest px-2 py-1 rounded ${isOwner ? "bg-velta-ink text-white" : "bg-velta-bg border border-velta-border"}`}>
                      <Shield className="w-3 h-3" /> {isOwner ? "Titolare" : "Staff"}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex flex-wrap gap-1 max-w-xs">
                      {(Array.isArray(perms) ? perms : []).slice(0, 4).map(p => {
                        const def = PERMS.find(x => x.key === p);
                        return <span key={p} className="text-[10px] font-mono uppercase tracking-widest bg-velta-bg border border-velta-border px-1.5 py-0.5 rounded">{def?.label || p}</span>;
                      })}
                      {perms.length > 4 && <span className="text-[10px] text-velta-mute">+{perms.length - 4}</span>}
                    </div>
                  </td>
                  <td className="px-5 py-4 text-xs font-mono">{modesText}</td>
                  <td className="px-5 py-4 text-xs font-mono" data-testid={`staff-stores-${u.id}`}>{isOwner || !u.store_ids?.length ? "Tutte" : `${u.store_ids.length} sed${u.store_ids.length === 1 ? "e" : "i"}`}</td>
                  <td className="px-5 py-4">
                    {u.has_pin ? <span className="inline-flex items-center gap-1 text-xs font-mono bg-[#2F9E77]/10 text-[#2F9E77] px-2 py-1 rounded">PIN ✓</span> : <span className="text-xs text-velta-mute">—</span>}
                  </td>
                  <td className="px-5 py-4">
                    <span className={`text-xs font-medium ${u.disabled ? "text-velta-error" : "text-velta-success"}`}>{u.disabled ? "Disattivato" : "Attivo"}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing && (
        <StaffModal
          data={editing}
          presets={presets}
          tenantModes={tenantModes}
          stores={stores}
          onClose={() => setEditing(null)}
          onSave={save}
        />
      )}
    </div>
  );
}

function StaffModal({ data, presets, tenantModes, stores = [], onClose, onSave }) {
  const principal = data.role === "client_owner" || data.role === "client";
  const [f, setF] = useState(() => {
    const perms = Array.isArray(data.permissions) ? [...data.permissions] : [];
    if (perms.includes("prezzi_acquisto") && !perms.includes("can_view_purchase_prices")) perms.push("can_view_purchase_prices");
    return { ...data, permissions: perms, allowed_modes: data.allowed_modes === undefined ? null : data.allowed_modes, store_ids: data.store_ids ?? null };
  });
  const [showPresets, setShowPresets] = useState(false);

  const togglePerm = (key) => {
    const cur = f.permissions || [];
    let next = cur.includes(key) ? (Array.isArray(cur) ? cur : []).filter(x => x !== key) : [...cur, key];
    // Il flag "Visualizza prezzo di acquisto" ha senso solo con accesso al magazzino
    if (key === "magazzino" && !next.includes("magazzino")) next = (Array.isArray(next) ? next : []).filter(x => x !== "prezzi_acquisto" && x !== "can_view_purchase_prices");
    if (key === "can_view_purchase_prices") {
      if (next.includes("can_view_purchase_prices") && !next.includes("prezzi_acquisto")) next = [...next, "prezzi_acquisto"];
      if (!next.includes("can_view_purchase_prices")) next = (Array.isArray(next) ? next : []).filter(x => x !== "prezzi_acquisto");
    }
    setF({ ...f, permissions: next });
  };

  const applyPreset = (presetKey) => {
    const preset = presets[presetKey];
    if (!preset) return;
    setF({ ...f, permissions: [...preset.permissions], allowed_modes: null });
    setShowPresets(false);
    toast.success(`Preset "${preset.label}" applicato`);
  };

  const toggleMode = (mode) => {
    const cur = f.allowed_modes;
    if (cur == null) {
      // se "Tutte", e l'utente toglie un mode → start with all tenant modes minus that one
      setF({ ...f, allowed_modes: (Array.isArray(tenantModes) ? tenantModes : []).filter(m => m !== mode) });
    } else if (cur.includes(mode)) {
      setF({ ...f, allowed_modes: (Array.isArray(cur) ? cur : []).filter(m => m !== mode) });
    } else {
      const next = [...cur, mode];
      // se ora include tutte le mode, riportiamo a null (Tutte)
      setF({ ...f, allowed_modes: next.length === tenantModes.length ? null : next });
    }
  };

  const isAllModes = f.allowed_modes == null;
  const allowedSet = new Set(f.allowed_modes || []);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50 overflow-auto" onClick={onClose}>
      <div className="bg-white rounded-md w-full max-w-2xl my-8 max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-velta-border shrink-0">
          <h3 className="font-heading text-2xl font-semibold">{principal ? "Modifica titolare" : (f.id ? "Modifica utente" : "Nuovo utente")}</h3>
          <button onClick={onClose} data-testid="staff-modal-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-auto p-6 space-y-5">
          {/* Anagrafica */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-1">Nome *</label>
              <input data-testid="staff-modal-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="h-11 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]" />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-1">Email *</label>
              <input data-testid="staff-modal-email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="h-11 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-1">Password {f.id && "(vuoto = invariata)"}</label>
            <input data-testid="staff-modal-password" type="password" minLength={6} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="h-11 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]" />
          </div>

          {/* PIN operatore */}
          <div className="bg-gradient-to-br from-[#2F9E77]/5 to-transparent border border-[#2F9E77]/30 rounded-md p-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-velta-mute">PIN rapido tablet</div>
                <div className="text-xs text-velta-mute mt-1">PIN numerico univoco a 4 cifre per palmare e cassa{f.has_pin ? <span className="text-[#2F9E77] font-medium"> · PIN impostato ✓</span> : null}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                data-testid="staff-modal-pin"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength="4"
                value={f.pin || ""}
                onChange={(e) => setF({ ...f, pin: e.target.value.replace(/[^0-9]/g, ""), clear_pin: false })}
                placeholder={f.has_pin ? "•••• (lascia vuoto per non cambiare)" : "es. 1234"}
                className="h-11 w-32 bg-white border border-velta-border rounded-md px-3 font-mono font-bold text-xl tracking-widest outline-none focus:border-[#2F9E77]"
              />
              {f.has_pin && (
                <button
                  type="button"
                  data-testid="staff-modal-pin-clear"
                  onClick={() => setF({ ...f, pin: "", clear_pin: true })}
                  className="h-9 px-3 text-xs border border-velta-error/30 text-velta-error rounded-md font-medium hover:bg-red-50"
                >
                  Rimuovi PIN
                </button>
              )}
            </div>
          </div>

          {principal && (
            <p className="text-sm text-velta-mute">Qui cambi nome, email e password del titolare. Questo account non si disattiva e non si cancella.</p>
          )}

          {!principal && (<>
          {/* Preset rapidi */}
          <div className="bg-velta-bg border border-velta-border rounded-md p-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-velta-mute">Preset rapidi</div>
                <div className="text-xs text-velta-mute mt-1">Configura permessi standard con un click</div>
              </div>
              <button
                data-testid="staff-presets-toggle"
                onClick={() => setShowPresets(!showPresets)}
                className="h-9 px-3 bg-white border border-velta-border rounded-md text-xs font-medium inline-flex items-center gap-1 hover:border-velta-ink"
              >
                <Zap className="w-3.5 h-3.5" /> Applica preset <ChevronDown className={`w-3 h-3 transition ${showPresets ? "rotate-180" : ""}`} />
              </button>
            </div>
            {showPresets && (
              <div className="grid grid-cols-2 gap-2 mt-3">
                {Object.entries(presets).map(([key, preset]) => (
                  <button
                    key={key}
                    data-testid={`staff-preset-${key}`}
                    onClick={() => applyPreset(key)}
                    className="text-left bg-white border border-velta-border rounded-md p-3 hover:border-velta-ink"
                  >
                    <div className="font-semibold text-sm">{preset.label}</div>
                    <div className="text-xs text-velta-mute mt-1">{preset.description}</div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {(Array.isArray(preset.permissions) ? preset.permissions : []).slice(0, 3).map(p => {
                        const def = PERMS.find(x => x.key === p);
                        return <span key={p} className="text-[9px] font-mono uppercase bg-velta-bg px-1 py-0.5 rounded">{def?.label || p}</span>;
                      })}
                      {preset.permissions.length > 3 && <span className="text-[9px] text-velta-mute">+{preset.permissions.length - 3}</span>}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Permessi */}
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-3">Permessi ({(f.permissions || []).length})</label>
            <div className="grid grid-cols-2 gap-2">
              {(Array.isArray(PERMS) ? PERMS : []).filter((p) => permOffered(p.key, tenantModes)).filter(p => p.key !== "can_view_purchase_prices" || (f.permissions || []).includes("magazzino")).map(p => {
                const Icon = p.icon;
                const active = (f.permissions || []).includes(p.key);
                return (
                  <label
                    key={p.key}
                    data-testid={`staff-perm-${p.key}`}
                    className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer text-sm transition ${active ? "bg-velta-ink text-white border-velta-ink" : "bg-white border-velta-border hover:border-velta-ink"}`}
                  >
                    <input type="checkbox" className="sr-only" checked={active} onChange={() => togglePerm(p.key)} />
                    <Icon className="w-4 h-4 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium leading-tight">{p.label}</div>
                      <div className={`text-xs leading-tight mt-0.5 ${active ? "text-white/70" : "text-velta-mute"}`}>{p.desc}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Modalità POS consentite */}
          {tenantModes.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-xs font-mono uppercase tracking-widest text-velta-mute">Modalità POS consentite</label>
                <label className="flex items-center gap-2 text-xs">
                  <input
                    data-testid="staff-modes-all"
                    type="checkbox"
                    checked={isAllModes}
                    onChange={(e) => setF({ ...f, allowed_modes: e.target.checked ? null : [] })}
                  />
                  <span className="font-medium">Tutte le {tenantModes.length} modalità</span>
                </label>
              </div>
              {!isAllModes && (
                <div className="flex flex-wrap gap-2">
                  {(Array.isArray(tenantModes) ? tenantModes : []).map(m => {
                    const active = allowedSet.has(m);
                    return (
                      <button
                        key={m}
                        type="button"
                        data-testid={`staff-mode-${m}`}
                        onClick={() => toggleMode(m)}
                        className={`h-9 px-3 rounded-full text-xs font-medium border capitalize transition ${active ? "bg-[#2F9E77] text-white border-[#2F9E77]" : "bg-white border-velta-border hover:border-velta-ink"}`}
                      >
                        {m}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Sedi consentite */}
          {stores.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-xs font-mono uppercase tracking-widest text-velta-mute flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Sedi consentite</label>
                <label className="flex items-center gap-2 text-xs">
                  <input
                    data-testid="staff-stores-all"
                    type="checkbox"
                    checked={f.store_ids == null}
                    onChange={(e) => setF({ ...f, store_ids: e.target.checked ? null : [] })}
                  />
                  <span className="font-medium">Tutte le {stores.length} sedi</span>
                </label>
              </div>
              {f.store_ids != null && (
                <div className="flex flex-wrap gap-2">
                  {(Array.isArray(stores) ? stores : []).map((s) => {
                    const active = (f.store_ids || []).includes(s.id);
                    return (
                      <button
                        key={s.id}
                        type="button"
                        data-testid={`staff-store-${s.id}`}
                        onClick={() => setF({ ...f, store_ids: active ? (Array.isArray(f.store_ids) ? f.store_ids : []).filter((x) => x !== s.id) : [...(f.store_ids || []), s.id] })}
                        className={`h-9 px-3 rounded-full text-xs font-medium border transition ${active ? "bg-[#1E2D5B] text-white border-[#1E2D5B]" : "bg-white border-velta-border hover:border-velta-ink"}`}
                      >
                        {s.name}
                      </button>
                    );
                  })}
                  <p className="w-full text-[11px] text-velta-mute">L'operatore vedrà appuntamenti e dati solo delle sedi selezionate. Nessuna selezione = tutte le sedi.</p>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-2 text-sm pt-2 border-t border-velta-border">
            <input data-testid="staff-modal-disabled" type="checkbox" checked={f.disabled || false} onChange={(e) => setF({ ...f, disabled: e.target.checked })} />
            <span>Utente disattivato (non può fare login)</span>
          </div>
          </>)}
        </div>

        <div className="p-6 border-t border-velta-border bg-velta-bg shrink-0 flex gap-3">
          <button onClick={onClose} className="flex-1 h-12 border border-velta-border rounded-md font-medium hover:border-velta-ink">Annulla</button>
          <button data-testid="staff-modal-save" onClick={() => onSave(f)} className="flex-1 h-12 bg-[#1E2D5B] hover:bg-[#162244] text-white rounded-md font-medium btn-tactile">
            {f.id ? "Salva modifiche" : "Crea utente"}
          </button>
        </div>
      </div>
    </div>
  );
}
